import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  useRouteError,
} from "react-router-dom";
import { useMemo } from "react";

import CheckVersion from "./components/CheckVersion";
import LayoutMain from "./layouts/LayoutMain";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PrivateRoute } from "./components/PrivateRoute";
import RoutesShared from "./userRoutes/RoutesUserShared";
import RoutesUserGoon from "@/userRoutes/RoutesUserGoon";
import RoutesUserLoggedOut from "@/userRoutes/RoutesUserLoggedOut";
import { useLayout } from "./contexts/UseLayout";
import ErrorRouter from "./components/ErrorRouter/ErrorRouter";
import { PainelPage } from "./pages/UserGoOn/PainelCriacao/Painel";
import TesteTecnico from "./pages/UserLoggedOut/TesteTecnico";
import loadingAnimation from "./assets/loading_screen.json";
import Player from "lottie-react";
import {
  hasAdLinkAccess,
  hasGoOnAccess,
} from "@/components/utils/NiveisDeAcesso";

const router = (user: any) => {
  let userRoutes: any[] = [...RoutesUserGoon];

  function ErrorBoundaryLogged() {
    const isAuthenticated = !!localStorage.getItem("token");
    let error: any = useRouteError();
    console.log("error?.data", error);

    if (!!error?.data) {
      if (!!isAuthenticated) {
        // Redireciona para /ad-link se usuário só tem acesso Ad Link
        if (hasAdLinkAccess({ data: user }) && !hasGoOnAccess({ data: user })) {
          return <Navigate to="/ad-link" />;
        }
        return <Navigate to="/home" />;
      } else {
        return <Navigate to="/login" />;
      }
    }

    return <ErrorRouter />;
  }

  userRoutes = [...userRoutes, ...RoutesShared];
  return createBrowserRouter([
    {
      path: "/",
      errorElement: <ErrorBoundaryLogged />,
      children: [...RoutesUserLoggedOut],
    },
    {
      path: "/teste-tecnico",
      element: <TesteTecnico />,
    },
    {
      path: "/websocket-test",
      lazy: async () => {
        const { default: WebSocketTest } =
          await import("@/pages/UserShared/WebSocketTest/WebSocketTest");
        return { element: <WebSocketTest /> };
      },
    },
    {
      path: "/painel-senhas/cliente/:token",
      lazy: async () => {
        const { PainelSenhasCliente } =
          await import("@/pages/Public/PainelSenhasCliente");
        return { element: <PainelSenhasCliente /> };
      },
    },
    {
      path: "/painel-senhas/display/:token",
      lazy: async () => {
        const { PainelSenhasDisplay } =
          await import("@/pages/Public/PainelSenhasDisplay");
        return { element: <PainelSenhasDisplay /> };
      },
    },
    {
      path: "",
      errorElement: <ErrorBoundaryLogged />,
      element: <PrivateRoute />,
      children: [
        {
          path: "",
          element: <LayoutMain />,
          children: [...userRoutes],
        },
        {
          path: "/painel-criacao",
          element: <PainelPage />,
          // children: ,
        },
        {
          path: "/pi/:deskId",
          lazy: async () => {
            const { PiDownloadPage } =
              await import("@/pages/UserGoOn/Overviews/Campanhas/FluxoMidia/PiDownloadPage/PiDownloadPage");
            return { element: <PiDownloadPage /> };
          },
        },
        {
          path: "/painel-tv/:jornadaId",
          lazy: async () => {
            const { default: PainelTV } =
              await import("@/pages/UserGoOn/PainelTV/PainelTV");
            return { element: <PainelTV /> };
          },
        },
        {
          path: "/ad-link",
          lazy: async () => {
            const { AdLink } = await import("@/pages/AdLink");
            return { element: <AdLink /> };
          },
          children: [
            {
              path: "",
              lazy: async () => {
                const { CommunityPage } =
                  await import("@/pages/AdLink/CommunityPage/CommunityPage");
                return { element: <CommunityPage /> };
              },
            },
            {
              path: "home",
              lazy: async () => {
                const { HomePage } =
                  await import("@/pages/AdLink/HomePage/HomePage");
                return { element: <HomePage /> };
              },
            },
            {
              path: "minhas-publicacoes",
              lazy: async () => {
                const { MyPublications } =
                  await import("@/pages/AdLink/MyPublications/MyPublications");
                return { element: <MyPublications /> };
              },
            },
            {
              path: "vagas",
              lazy: async () => {
                const { Vagas } = await import("@/pages/AdLink/Vagas/Vagas");
                return { element: <Vagas /> };
              },
            },
            {
              path: "curriculo",
              lazy: async () => {
                const { default: Curriculo } =
                  await import("@/pages/AdLink/Curriculo");
                return { element: <Curriculo /> };
              },
            },
            {
              path: "perfil-profissional",
              element: <Navigate to="/ad-link/curriculo" replace />,
            },
            {
              path: "perfil/:id",
              lazy: async () => {
                const { default: UserProfile } =
                  await import("@/pages/AdLink/UserProfile");
                return { element: <UserProfile /> };
              },
            },
            {
              path: "empresa/configuracao",
              lazy: async () => {
                const { default: CompanyProfileEditor } = await import(
                  "@/pages/AdLink/CompanyProfile/CompanyProfileEditor"
                );
                return {
                  element: (
                    <ProtectedRoute recurso="empresas" permissao="editar">
                      <CompanyProfileEditor />
                    </ProtectedRoute>
                  ),
                };
              },
            },
            {
              path: "empresa/:slug",
              lazy: async () => {
                const { default: CompanyProfilePage } = await import(
                  "@/pages/AdLink/CompanyProfile/CompanyProfile"
                );
                return { element: <CompanyProfilePage /> };
              },
            },
            {
              path: "minhas-candidaturas",
              lazy: async () => {
                const { default: MinhasCandidaturas } =
                  await import("@/pages/AdLink/MinhasCandidaturas");
                return { element: <MinhasCandidaturas /> };
              },
            },
            {
              path: "meus-salvos",
              lazy: async () => {
                const { TalentosSalvos } =
                  await import("@/pages/Recrutamento/TalentosSalvos/TalentosSalvos");
                return { element: <TalentosSalvos /> };
              },
            },
          ],
        },
        {
          path: "/dash-recrutamento",
          element: <LayoutMain />,
          children: [
            {
              path: "",
              lazy: async () => {
                const { DashRecrutamento } =
                  await import("@/pages/Recrutamento/DashRecrutamento/DashRecrutamento");
                return {
                  element: (
                    <ProtectedRoute recurso="vagas" permissao="visualizar">
                      <DashRecrutamento />
                    </ProtectedRoute>
                  ),
                };
              },
            },
          ],
        },
        {
          path: "/talentos-salvos",
          element: <LayoutMain />,
          children: [
            {
              path: "",
              lazy: async () => {
                const { TalentosSalvos } =
                  await import("@/pages/Recrutamento/TalentosSalvos/TalentosSalvos");
                return {
                  element: (
                    <ProtectedRoute recurso="vagas" permissao="visualizar">
                      <TalentosSalvos />
                    </ProtectedRoute>
                  ),
                };
              },
            },
          ],
        },
        {
          path: "/meus-salvos",
          element: <LayoutMain />,
          children: [
            {
              path: "",
              lazy: async () => {
                const { TalentosSalvos } =
                  await import("@/pages/Recrutamento/TalentosSalvos/TalentosSalvos");
                return { element: <TalentosSalvos /> };
              },
            },
          ],
        },
        {
          path: "/criar-nova-vaga",
          element: <LayoutMain />,
          children: [
            {
              path: "",
              lazy: async () => {
                const { CriarVaga } =
                  await import("@/pages/Recrutamento/CriarVaga/CriarVaga");
                return {
                  element: (
                    <ProtectedRoute recurso="vagas" permissao="criar">
                      <CriarVaga />
                    </ProtectedRoute>
                  ),
                };
              },
            },
          ],
        },
        {
          path: "/criar-nova-vaga/:idEdit",
          element: <LayoutMain />,
          children: [
            {
              path: "",
              lazy: async () => {
                const { CriarVaga } =
                  await import("@/pages/Recrutamento/CriarVaga/CriarVaga");
                return {
                  element: (
                    <ProtectedRoute recurso="vagas" permissao="editar">
                      <CriarVaga />
                    </ProtectedRoute>
                  ),
                };
              },
            },
          ],
        },
      ],
    },
  ]);
};

function GlobalLoading() {
  return (
    <div className="w-screen h-screen bg-azul-400 flex justify-center items-center">
      <Player
        autoplay
        loop
        animationData={loadingAnimation}
        style={{ height: "100%", width: "100%" }}
      />
    </div>
  );
}

function ErrorGlobal() {
  return (
    <div className="w-screen h-screen bg-red-500 flex justify-center items-center text-white">
      Houve um erro.. :(
    </div>
  );
}

export default function RoutesMain() {
  const contextLayout = useLayout();

  // Memoizar o router para que ele só seja recriado quando o usuário
  // realmente mudar (login/logout), e NÃO a cada mudança de menuOpen/chats/etc.
  // Sem isso, createBrowserRouter() criava um router NOVO a cada render,
  // causando desmontagem e remontagem de toda a árvore de componentes.
  const memoizedRouter = useMemo(
    () => router(contextLayout?.userLogged?.data),
    [contextLayout?.userLogged?.data?.id],
  );

  if (contextLayout.fullLoading) {
    return <GlobalLoading />;
  }
  if (contextLayout.errorGlobal) {
    return <ErrorGlobal />;
  }

  return (
    <>
      <RouterProvider
        router={memoizedRouter}
        // fallbackElement={<div>Carregando...</div>}
      />
      <CheckVersion />
    </>
  );
}
